import React from 'react';
import { Loader } from 'react-feather';

import { Contact } from '../../types/contactTypes';

interface ContactListTableProps {
  contacts: Contact[];
  isLoading: boolean;
  hasError: boolean;
  isEmpty: boolean;
  error?: unknown;
  onRowClick?: (contactId: number) => void;
}

// 날짜 포맷팅 함수 (ISO -> YYYY.M.D 형식)
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}.${month}.${day}`;
  } catch {
    return dateString;
  }
};

// 상태 표시 컴포넌트
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const isRead = status.toLowerCase() === 'read';

  return (
    <div
      className={`inline rounded-full px-4 py-1 text-center text-xs font-medium tracking-wider text-white ${
        isRead ? 'bg-[#7676E9]' : 'bg-[#FF6B6B]'
      }`}
      aria-label={status}
    >
      {isRead ? 'Read' : 'Unread'}
    </div>
  );
};

const ContactListTable: React.FC<ContactListTableProps> = ({
  contacts,
  isLoading,
  hasError,
  isEmpty,
  error,
  onRowClick,
}) => {
  // 로딩 상태 렌더링
  if (isLoading) {
    return (
      <div className="relative w-full">
        <table className="min-w-full table-fixed border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                scope="col"
                className="sticky top-0 w-28 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Date
              </th>
              <th
                scope="col"
                className="sticky top-0 w-48 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Name
              </th>
              <th
                scope="col"
                className="sticky top-0 w-72 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Email
              </th>
              <th
                scope="col"
                className="sticky top-0 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Status
              </th>
            </tr>
          </thead>
        </table>
        <div className="flex min-h-[calc(100vh-56px)] w-full items-center justify-center bg-white">
          <div className="flex items-center gap-2">
            <Loader className="animate-spin text-[#1D1D1D]" size={20} />
            <span className="text-sm text-[#666666]">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 렌더링
  if (hasError) {
    const errorMessage =
      error instanceof Error ? error.message : 'An error occurred';

    return (
      <div className="relative w-full">
        <table className="min-w-full table-fixed border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                scope="col"
                className="sticky top-0 w-28 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Date
              </th>
              <th
                scope="col"
                className="sticky top-0 w-48 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Name
              </th>
              <th
                scope="col"
                className="sticky top-0 w-72 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Email
              </th>
              <th
                scope="col"
                className="sticky top-0 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Status
              </th>
            </tr>
          </thead>
        </table>
        <div className="flex min-h-[calc(100vh-56px)] w-full items-center justify-center bg-white">
          <div className="text-center">
            <p className="mb-2 text-sm text-[#FF6B6B]">Error occurred</p>
            <p className="text-xs text-[#666666]">{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded bg-[#1D1D1D] px-4 py-2 text-sm text-white"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 빈 목록 상태 렌더링
  if (isEmpty) {
    return (
      <div className="relative w-full">
        <table className="min-w-full table-fixed border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                scope="col"
                className="sticky top-0 w-28 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Date
              </th>
              <th
                scope="col"
                className="sticky top-0 w-48 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Name
              </th>
              <th
                scope="col"
                className="sticky top-0 w-72 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Email
              </th>
              <th
                scope="col"
                className="sticky top-0 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
              >
                Status
              </th>
            </tr>
          </thead>
        </table>
        <div className="flex min-h-[calc(100vh-56px)] w-full items-center justify-center bg-white">
          <div className="text-center">
            <p className="text-sm text-[#666666]">No contacts found</p>
          </div>
        </div>
      </div>
    );
  }

  // 정상 데이터 렌더링
  return (
    <div className="relative w-full">
      <table className="min-w-full table-fixed border-collapse">
        <thead className="sticky top-0 z-20">
          <tr>
            <th
              scope="col"
              className="sticky top-0 w-28 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
            >
              Date
            </th>
            <th
              scope="col"
              className="sticky top-0 w-48 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
            >
              Name
            </th>
            <th
              scope="col"
              className="sticky top-0 w-72 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
            >
              Email
            </th>
            <th
              scope="col"
              className="sticky top-0 whitespace-nowrap border-b border-[#DEDEDE] bg-[#EFEFEF] px-5 py-2 text-left text-sm font-medium text-[#707070]"
            >
              Status
            </th>
          </tr>
        </thead>
      </table>
      <div className="min-h-[calc(100vh-56px)] w-full overflow-y-auto">
        <table className="min-w-full table-fixed border-collapse">
          <tbody>
            {contacts.map((contact) => (
              <tr
                key={contact.id}
                className="cursor-pointer transition-colors hover:bg-gray-50"
                onClick={() => onRowClick?.(contact.id)}
              >
                <td className="w-28 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-3 text-left align-middle text-sm font-normal text-[#1D1D1D]">
                  {formatDate(contact.createdAt)}
                </td>
                <td className="w-48 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-3 text-left align-middle text-sm font-normal text-[#1D1D1D]">
                  {contact.representativeName}
                </td>
                <td className="w-72 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-3 text-left align-middle text-sm font-normal text-[#1D1D1D]">
                  {contact.email}
                </td>
                <td className="whitespace-nowrap border-b border-gray-300 bg-white px-5 py-3 text-left align-middle text-sm font-normal text-[#1D1D1D]">
                  <StatusBadge status={contact.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactListTable;
